import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import { Card, CardBody, Modal, ModalHeader, ModalBody, ModalFooter, Input } from 'reactstrap';
import { toast } from 'react-toastify';
import { getTickets, addTicketComment, changeTicketStatus, historyTicketStatus } from '../../services/tickets';
import './tickets.css';
import { RiProgress1Line } from 'react-icons/ri';
import { FaRegBuilding, FaRegCommentDots } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Progress from './progressStatus';
import { BsBookmarkCheckFill } from "react-icons/bs";
import { ClockHistory, PlayCircle } from 'react-bootstrap-icons';
import { VscMailRead } from 'react-icons/vsc';
import LoadingScreen from 'src/Loading/LoadingScreen';
import { MdHistory, MdOutlineModeComment } from 'react-icons/md';
import { LuCalendarDays } from 'react-icons/lu';
import { FiSearch, FiUserCheck } from 'react-icons/fi';
import { IoCheckmarkDoneCircleOutline } from 'react-icons/io5';
import { getUserInfo, getUserRolePermissions } from '../../services/userServicer';
import { useSearchQuery } from 'src/hook/useSearchQuery';
import { usePageHeadingOverride } from '@/components/Layout/PageHeadingContext';
type Step = {
  status: "pending" | "started" | "done";
  label: string;
  icon: React.ReactNode;
};

type StatusHistory = {
  userId: string;
  ticketId: string;
  status: string;
  date: string;
  user: {
    id: string;
    name: string;
  };
  ticket: {
    id: string;
    tenantId: string;
    fromId: string;
    complianceId: string;
    code: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  };
};

/** Role API returns one object per page with flags on the same object (no nested `.permissions`). */
const normalizeTicketPage = (value: unknown) => String(value ?? '').trim().toLowerCase();
const permissionToBool = (value: unknown): boolean => {
  if (value === true) return true;
  if (value === false || value == null) return false;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === 'true' || v === '1' || v === 'yes';
  }
  return Boolean(value);
};

/** Page indices for pagination (with ellipses when there are many pages). */
function getVisibleTicketPages(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 1) return [1];
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages: (number | 'ellipsis')[] = [1];
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);
  if (left > 2) pages.push('ellipsis');
  for (let p = left; p <= right; p++) pages.push(p);
  if (right < total - 1) pages.push('ellipsis');
  pages.push(total);
  return pages;
}

const TicketsSupCompliance = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [perPage, setPerPage] = useState(9);
  const [currentPage, setCurrentPage] = useState(1);
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false); // State for comments modal
  const [isStatusHistoryModalOpen, setIsStatusHistoryModalOpen] = useState(false); // State for status history modal
  const [selectedComments, setSelectedComments] = useState<any[]>([]);
  const [selected_code, setSelected_code] = useState('');
  const [selected_id, setSelected_id] = useState('');
  const [newComment, setNewComment] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [statusHistory, setStatusHistory] = useState<StatusHistory[]>([]); // State for status history
  const [loading, setLoading] = useState(true); // State for loading screen
  const [activeTab, setActiveTab] = useState('card');
  const [permissions, setPermissions] = useState<any>(null);
  const [notAllowed, setNotAllowed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const didSkipFirstSearch = useRef(false);

  // Use the search query hook
  const { searchQuery, searchResultType, clearSearchQuery } = useSearchQuery();

  usePageHeadingOverride({ hidden: true });


  const steps: Step[] = useMemo(
    () => [
      { status: 'pending' as const, label: t('Tickets.Pending'), icon: <ClockHistory /> },
      { status: 'started' as const, label: t('Tickets.Started'), icon: <PlayCircle /> },
      { status: 'done' as const, label: t('Tickets.Done'), icon: <BsBookmarkCheckFill /> },
    ],
    [t]
  );

  const formatTicketDate = (value: string | undefined) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const ticketStatusLabel = (status: string) => {
    const s = (status || '').toLowerCase();
    const key =
      s === 'pending'
        ? 'Tickets.status_pending'
        : s === 'started'
          ? 'Tickets.status_started'
          : s === 'done'
            ? 'Tickets.status_done'
            : s === 'inactive'
              ? 'Tickets.status_inactive'
              : 'Tickets.Status';
    return t(key);
  };
  const [isModalOpen, setIsModalOpen] = useState(false);

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  // Toggle functions for each modal
  const toggleCommentsModal = () => setIsCommentsModalOpen(!isCommentsModalOpen);
  const toggleStatusHistoryModal = () => setIsStatusHistoryModalOpen(!isStatusHistoryModalOpen);

  const fetchTicketsData = async (searchQuery: string = '', mode: 'global' | 'table' = 'global') => {
    if (mode === 'global') {
      setLoading(true);
    } else {
      setTableLoading(true);
    }
    try {
      const response = await getTickets(searchQuery, perPage, currentPage);
      if (response && response.success) {
        setData(response.data);
        setTotalPages(response.totalPages);
      } else {
        if (!notAllowed) {
          toast.error(t('Tickets.Failed to fetch tickets data'));
        }
      }
    } catch (error: any) {
      if (!notAllowed) {
        toast.error(t('Tickets.Error fetching tickets data:'));
      }
    } finally {
      if (mode === 'global') {
        setLoading(false);
      } else {
        setTableLoading(false);
      }
    }
  };

  const handleStepClick = async (ticketId: string, stepIndex: number) => {
    const statusOrder = ["pending", "started", "done"] as const;
    const nextStatus = statusOrder[stepIndex - 1]; // Convert stepIndex to status

    try {
      const result = await changeTicketStatus(ticketId, nextStatus);
     
        toast.success(t("Tickets.ticket status updated "),result.status);
        fetchTicketsData(); // Refresh ticket data after updating the status

    } catch (error) {
      toast.error(t("Tickets.Error changing ticket status"));
    }
  };

  const handleAddComment = async () => {
   const user = await getUserInfo();
      if (!user?.id) return;
      let userId =  user.id || "";
      await addTicketComment(userId, tenantId, selected_id, newComment).then((response: any) => {
      if (response?.success) {
        toast.success(t('Tickets.comment send successfully'));
        // Set the ticket status to "started" after the first comment
        changeTicketStatus(selected_id, "started").then(() => {
          // Change status to "started"
        }).catch((error) => {
          toast.error(t("Tickets.Error changing ticket status"));
        });
        
      } else {
        toast.error(response?.message || t("Tickets.Failed to add comment"));
      }
    });
    setNewComment('');
    toggleCommentsModal();
    fetchTicketsData();
  };

  // Main effect to fetch data when dependencies change (excluding searchTerm; handled by debounce)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = await getUserInfo();
        if (!user?.id) {
          setNotAllowed(true);
          setPermissions(null);
          setLoading(false);
          return;
        }

        const response = await getUserRolePermissions(user.id);

        let allowed = false;
        if (
          response?.success &&
          response?.data?.permissions &&
          Array.isArray(response.data.permissions)
        ) {
          const ticketPerm = response.data.permissions.find(
            (p: { page?: string }) => normalizeTicketPage(p?.page) === 'tickets'
          );
          if (ticketPerm) {
            setPermissions(ticketPerm);
            allowed = permissionToBool(ticketPerm.show);
            setNotAllowed(!allowed);
          } else {
            setPermissions(null);
            setNotAllowed(true);
          }
        } else {
          setPermissions(null);
          setNotAllowed(true);
        }

        if (allowed) {
          await fetchTicketsData('', 'global');
          didSkipFirstSearch.current = true; // prevent immediate debounced fetch
        } else {
          // Suppress toast; show only inline message and stop global loader
          setLoading(false);
        }
      } catch (error) {
        setNotAllowed(true);
        // Suppress toast on permission errors; stop global loader so inline message shows
        setLoading(false);
      } finally {
        // keep global loading managed inside fetchTicketsData when allowed
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage, currentPage]);

  // Debounced search effect - fetch after typing stops; shows table-only loader
  useEffect(() => {
    if (notAllowed) {
      return;
    }
    if (didSkipFirstSearch.current) {
      didSkipFirstSearch.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const q = (searchTerm || '').trim();
      // Reset to first page when searching
      if (currentPage !== 1) setCurrentPage(1);
      fetchTicketsData(q, 'table');
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, notAllowed]);

  // Handle search query from global search (only set the term, debounce will fetch)
  useEffect(() => {
    if (searchQuery && searchResultType === 'tickets') {
      setSearchTerm(searchQuery);
      clearSearchQuery();
    }
  }, [searchQuery, searchResultType, clearSearchQuery]);
  

  const fetchStatusHistory = async (ticketId: string) => {
    try {
      const result = await historyTicketStatus(ticketId); // Pass the ticketId
      if (result.success) {
        setStatusHistory(result.data); // Set the fetched history
        toggleStatusHistoryModal(); // Open the status history modal
      } else {
        toast.error(t("Tickets.Failed to fetch status history"));
      }
    } catch (error) {
      toast.error(t("Tickets.Error fetching status history"));
    }
  };

  const totalPagesSafe = Math.max(totalPages || 1, 1);
  const visibleTicketPages = useMemo(
    () => getVisibleTicketPages(currentPage, totalPagesSafe),
    [currentPage, totalPagesSafe]
  );

  if (loading) {
    return (
      <div className="loading-screen" 
      style={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.8)'}}>
                     <div><LoadingScreen/></div>
                   </div>
    );
  }
  if (notAllowed) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <div
          className="alert alert-danger text-center"
          style={{
            maxWidth: "500px",
            width: "100%",
            padding: "32px 24px",
            borderRadius: "12px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            background: "#f8d7da",
            color: "#842029",
            fontSize: "1.2rem",
          }}
        >
          {t("You do not have permission to view this page.")}
        </div>
      </div>
    );
  }
  const canEditStatus = permissions?.edit;
  const canComment = permissions?.create;

  return (
    <div className=" tickets-sup-page allowTop">
      <header className="tickets-sup-page-header">
        <h1 className="tickets-sup-title">{t('Tickets.Tickets')}</h1>
        <p className="tickets-sup-subtitle">{t('Tickets.page_subtitle')}</p>
      </header>

      <div className="tickets-sup-main-surface">
        <div className="tickets-sup-main-toolbar">
          <div className="tickets-sup-search-wrap">
            <FiSearch className="tickets-sup-search-icon" size={18} aria-hidden />
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={String(t('Tickets.search_placeholder'))}
              className="tickets-sup-search-input"
              aria-label={t('Tickets.search_placeholder') as string}
            />
          </div>
          <div className="tickets-sup-view-toggle" role="tablist" aria-label={t('Tickets.Tickets') as string}>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'card'}
              className={activeTab === 'card' ? 'is-active' : ''}
              onClick={() => setActiveTab('card')}
            >
              {t('Tickets.Card View')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'list'}
              className={activeTab === 'list' ? 'is-active' : ''}
              onClick={() => setActiveTab('list')}
            >
              {t('Tickets.List View')}
            </button>
          </div>
        </div>

      {activeTab === 'card' && (
        <>
          {tableLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ padding: '2.5rem' }}>
              <div className="spinner-border text-warning" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="tickets-sup-grid">
              {data.map((card: any) => (
                <Card className="tickets-sup-card" key={card.id}>
                  <div className="tickets-sup-card-header">
                    {card.status === 'inactive' ? (
                      <span
                        className="badge rounded-pill"
                        style={{
                          backgroundColor: '#e2e3e5',
                          color: '#41464b',
                          fontSize: '0.8125rem',
                          fontWeight: 600,
                          padding: '0.4rem 0.75rem',
                        }}
                      >
                        {t('Tickets.Inactive')}
                      </span>
                    ) : (
                      <Progress
                        wrapClassName="tickets-sup-progress-strip"
                        steps={steps}
                        currentStep={steps.findIndex((step) => step.status === card.status) + 1}
                        onStepClick={(stepIndex) => {
                          if (canEditStatus) handleStepClick(card.id, stepIndex);
                        }}
                      />
                    )}
                  </div>
                  <CardBody>
                    <div className="tickets-sup-card-id">
                      <p className="tickets-sup-card-id-label">{t('Tickets.TicketsID')}</p>
                      <p className="tickets-sup-card-id-code">{card.code}</p>
                    </div>

                    <div className="tickets-sup-meta">
                      <div className="tickets-sup-meta-icon">
                        <FaRegBuilding size={16} aria-hidden />
                      </div>
                      <div className="min-w-0">
                        <div className="tickets-sup-meta-label">{t('Tickets.Organization')}</div>
                        <div className="tickets-sup-meta-value">{card?.tenant?.name || '—'}</div>
                      </div>
                    </div>

                    <div className="tickets-sup-meta">
                      <div className="tickets-sup-meta-icon">
                        <LuCalendarDays size={16} aria-hidden />
                      </div>
                      <div className="min-w-0">
                        <div className="tickets-sup-meta-label">{t('Tickets.created_label')}</div>
                        <div className="tickets-sup-meta-value">{formatTicketDate(card?.createdAt)}</div>
                      </div>
                    </div>

                    <div className="tickets-sup-meta">
                      <div className="tickets-sup-meta-icon">
                        <FiUserCheck size={16} aria-hidden />
                      </div>
                      <div className="min-w-0">
                        <div className="tickets-sup-meta-label">{t('Tickets.Assigned Role')}</div>
                        <div className="tickets-sup-meta-value" style={{ textTransform: 'capitalize' }}>
                          {card?.from?.name || '—'}
                        </div>
                      </div>
                    </div>
                  </CardBody>

                  <div className="tickets-sup-card-footer">
                    <button
                      type="button"
                      className="btn btn-viewComplianceTicket"
                      onClick={() => navigate(`/compliances/status/${card.complianceId}`)}
                    >
                      {t('Tickets.view_compliance')}
                    </button>
                    <div className="tickets-sup-footer-actions">
                      <div className="d-flex align-items-center gap-1">
                        <MdOutlineModeComment size={18} className="text-secondary" aria-hidden />
                        <button
                          type="button"
                          className="btn btn-link p-0 text-decoration-none"
                          onClick={() => {
                            setSelectedComments(card.ticketComments);
                            setSelected_code(card.code);
                            setSelected_id(card.id);
                            setTenantId(card.tenantId);
                            toggleCommentsModal();
                          }}
                        >
                          {card?.ticketComments?.length > 0
                            ? `${card.ticketComments.length} ${t('Tickets.comments')}`
                            : t('Tickets.no_comments')}
                        </button>
                      </div>
                      <button
                        type="button"
                        className="btn btn-link p-0 text-secondary"
                        aria-label={t('Tickets.Status History') as string}
                        onClick={() => fetchStatusHistory(card.id)}
                      >
                        <MdHistory size={20} />
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

{activeTab === 'list' && (
          <>
              <div className="tickets-sup-list-panel">
          <div className="table-responsive">
          <table className="styled-table tickets-sup-table">
  <thead>
    <tr>
      <th>{t('Tickets.TicketsID')}</th>
      <th>{t('Tickets.Status')}</th>
      <th>{t('Tickets.Assigned Role')}</th>
      <th>{t('Tickets.Organization')}</th>
      <th>{t('Tickets.created_label')}</th>
      <th>{t('Tickets.Compliance')}</th>
      <th>{t('Tickets.Actions')}</th>
    </tr>
  </thead>
  <tbody>
    {tableLoading ? (
      <tr>
        <td colSpan={7}>
          <div className="d-flex justify-content-center align-items-center" style={{ padding: '2rem' }}>
            <div className="spinner-border text-warning" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </td>
      </tr>
    ) : (
    data.map((card: any) => (
      <tr key={card.id}>
        {/* Ticket ID */}
        <td>{card.code}</td>
        {/* Status */}
        <td>
        <span
  className="px-3 py-1 align-items-center gap-1"
  style={{
    backgroundColor:
      card.status === 'done'
        ? '#cff7e3'
        : card.status === 'started'
        ? '#e0e1e2'
        : card.status === 'inactive'
        ? '#e2e3e5'
        : '#f8d7da',
    color:
      card.status === 'done'
        ? '#198754'
        : card.status === 'started'
        ? '#1d6efd'
        : card.status === 'inactive'
        ? '#41464b'
        : '#dc3545',
    fontSize: '1.1rem',
    borderRadius: '16px',
    border: `1px solid ${
      card.status === 'done'
        ? '#198754'
        : card.status === 'started'
        ? '#1d6efd'
        : card.status === 'inactive'
        ? '#d3d6d8'
        : '#dc3545'
    }`,
    display: 'inline-flex', 
    alignItems: 'center', 
  }}
>
  {card.status === 'done' && <IoCheckmarkDoneCircleOutline  />}
  {card.status === 'started' && <RiProgress1Line />}
  {ticketStatusLabel(card.status)}
</span>
        </td>
        {/* Assigned Role */}
        <td style={{ textTransform: 'capitalize' }}>
          {card?.from?.name}
        </td>
        {/* Organization */}
        <td>{card?.tenant?.name}</td>
        {/* Completed Date */}
        <td>{formatTicketDate(card?.createdAt)}</td>
        {/* Compliance Button */}
        <td>
        <button className="btn btn-viewComplianceTicket mr-2 mt-2" 
        onClick={() => navigate(`/compliances/status/${card.complianceId}`)}>
                        {t('Tickets.view_compliance')}
                      </button>
        </td>
        {/* Actions */}
        <td>
        {canEditStatus && (
          <MdHistory size={18} className="mx-2" onClick={() => fetchStatusHistory(card.id)} />
        )}
          <MdOutlineModeComment size={18} className="me-1"
            onClick={() => {
              setSelectedComments(card.ticketComments);
              setSelected_code(card.code);
              setSelected_id(card.id);
              setTenantId(card.tenantId);
              toggleCommentsModal();
            }}
             />
        </td>
      </tr>
    )))
    }
  </tbody>
</table>
          </div>
</div>
          </>

        )}

          <nav className="tickets-sup-pagination" aria-label={String(t('Tickets.page'))}>
            <div className="tickets-sup-pagination-row">
              <button
                type="button"
                className="tickets-sup-page-nav"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                ‹ {t('institution.pagination_previous')}
              </button>
              <div className="tickets-sup-page-nums" role="presentation">
                {visibleTicketPages.map((item, idx) =>
                  item === 'ellipsis' ? (
                    <span key={`ellipsis-${idx}`} className="tickets-sup-page-ellipsis" aria-hidden>
                      …
                    </span>
                  ) : currentPage === item ? (
                    <span key={item} className="tickets-sup-page-num is-active" aria-current="page">
                      {item}
                    </span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      className="tickets-sup-page-num"
                      onClick={() => setCurrentPage(item)}
                    >
                      {item}
                    </button>
                  )
                )}
              </div>
              <button
                type="button"
                className="tickets-sup-page-nav"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPagesSafe))}
                disabled={currentPage >= totalPagesSafe}
              >
                {t('institution.pagination_next')} ›
              </button>
            </div>
            <p className="tickets-sup-page-meta">
              {t('Tickets.pagination_summary', { current: currentPage, total: totalPagesSafe })}
            </p>
          </nav>
      </div>

          {/* Comments Modal */}
          <Modal isOpen={isCommentsModalOpen} toggle={toggleCommentsModal} centered className="tickets-sup-modal">
            <ModalHeader toggle={toggleCommentsModal} className="tickets-sup-modal-header">
              {t('Tickets.comments_for')} #{selected_code}
            </ModalHeader>
            <ModalBody className="tickets-sup-modal-body">
              <div className="tickets-sup-comments-scroll">
                {selectedComments?.length > 0 ? (
                  selectedComments?.map((comment: any, idx: number) => (
                    <div key={idx} className="tickets-sup-comment-item">
                      <div className="tickets-sup-comment-row">
                        {comment?.from?.name ? (
                          <div className="tickets-sup-comment-avatar" aria-hidden>
                            {comment.from.name.charAt(0).toUpperCase()}
                          </div>
                        ) : (
                          <div className="tickets-sup-comment-avatar tickets-sup-comment-avatar--muted" aria-hidden>
                            ?
                          </div>
                        )}
                        <div className="tickets-sup-comment-body">
                          <div className="tickets-sup-comment-meta">
                            <span className="tickets-sup-comment-author">{comment?.from?.name || '—'}</span>
                            <time className="tickets-sup-comment-time" dateTime={comment.createdAt}>
                              {new Date(comment.createdAt).toLocaleString()}
                            </time>
                          </div>
                          <p className="tickets-sup-comment-text">{comment.comment || t('view_control.no_text_comment')}</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="tickets-sup-comments-empty" role="status">
                    <VscMailRead size={28} aria-hidden className="tickets-sup-comments-empty-icon" />
                    <span>{t('Tickets.no_comments')}</span>
                  </div>
                )}
              </div>

              {canComment ? (
                <div className="tickets-sup-modal-field tickets-sup-modal-field--tight">
                  <label className="tickets-sup-modal-label" htmlFor="tickets-new-comment">
                    {t('Tickets.write_a_comment')}
                  </label>
                  <Input
                    id="tickets-new-comment"
                    type="textarea"
                    rows={4}
                    placeholder={t('Tickets.write_a_comment') as string}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="tickets-sup-modal-textarea"
                  />
                </div>
              ) : null}
            </ModalBody>
            <ModalFooter className="tickets-sup-modal-footer tickets-sup-modal-footer--actions">
              <button type="button" className="tickets-sup-modal-btn tickets-sup-modal-btn--outline" onClick={toggleCommentsModal}>
                {t('Tickets.close')}
              </button>
              {canComment ? (
                <button type="button" onClick={() => void handleAddComment()} className="tickets-sup-modal-btn tickets-sup-modal-btn--primary">
                  {t('Tickets.add_comment')}
                </button>
              ) : null}
            </ModalFooter>
          </Modal>

          {/* Status History Modal */}
          <Modal isOpen={isStatusHistoryModalOpen} toggle={toggleStatusHistoryModal} centered className="tickets-sup-modal">
            <ModalHeader toggle={toggleStatusHistoryModal} className="tickets-sup-modal-header">
              {t('Tickets.Status History')}
            </ModalHeader>
            <ModalBody className="tickets-sup-modal-body">
              {statusHistory.length > 0 ? (
                <ul className="tickets-sup-history-list">
                  {statusHistory.map((history, index) => (
                    <li key={index} className="tickets-sup-history-item">
                      <div className="tickets-sup-history-top">
                        <span className="tickets-sup-history-code">{history.ticket.code}</span>
                        <span className="tickets-sup-history-status">{history.status}</span>
                      </div>
                      <p className="tickets-sup-history-meta">
                        {t('Tickets.Changed By')} <strong>{history.user.name}</strong>
                      </p>
                      <time className="tickets-sup-history-time" dateTime={history.date}>
                        {new Date(history.date).toLocaleString()}
                      </time>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="tickets-sup-history-empty" role="status">
                  {t('Tickets.No status history available.')}
                </div>
              )}
            </ModalBody>
            <ModalFooter className="tickets-sup-modal-footer tickets-sup-modal-footer--actions">
              <button type="button" className="tickets-sup-modal-btn tickets-sup-modal-btn--outline" onClick={toggleStatusHistoryModal}>
                {t('Tickets.Close')}
              </button>
            </ModalFooter>
          </Modal>

    </div>
  );
};

export default withTranslation()(TicketsSupCompliance);